import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer, Typography, IconButton, Button, Fab,
  Badge, CircularProgress, TextField, MenuItem,
  Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { toast } from "react-toastify";

import { listMyTickets, createTicket, getMyCounts } from "../../services/supportTicketService";
import SupportTicketChat from "../SupportTicketChat";

const PRIORITY_STYLES = {
  baixa: { background: "#dcfce7", color: "#166534" },
  media: { background: "#fef9c3", color: "#92400e" },
  alta: { background: "#fed7aa", color: "#9a3412" },
  urgente: { background: "#fee2e2", color: "#991b1b" },
};

const STATUS_STYLES = {
  aberto: { background: "#e0f2fe", color: "#0369a1" },
  em_atendimento: { background: "#f3e8ff", color: "#7c3aed" },
  aguardando_resposta: { background: "#fef9c3", color: "#92400e" },
  resolvido: { background: "#dcfce7", color: "#166534" },
  fechado: { background: "#f1f5f9", color: "#64748b" },
};

const STATUS_LABELS = {
  aberto: "Aberto",
  em_atendimento: "Em atendimento",
  aguardando_resposta: "Aguardando",
  resolvido: "Resolvido",
  fechado: "Fechado",
};

const CATEGORY_LABELS = {
  tecnico: "Técnico", financeiro: "Financeiro", conta: "Conta",
  funcionalidade: "Funcionalidade", outro: "Outro",
};

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 1200,
    width: 56,
    height: 56,
    boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
  },
  drawerPaper: {
    width: 380,
    maxWidth: "100vw",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    background: theme.palette.primary.main,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: { color: "#fff", fontWeight: 700, fontSize: "1rem" },
  headerSubtitle: { color: "rgba(255,255,255,0.75)", fontSize: "0.72rem" },
  body: {
    flex: 1,
    overflowY: "auto",
    background: "#f8fafc",
  },
  footer: {
    padding: "12px 16px",
    background: "#fff",
    borderTop: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  // Welcome state
  welcomeBox: {
    padding: "36px 24px 24px",
    textAlign: "center",
  },
  welcomeIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: `${theme.palette.primary.main}22`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  welcomeTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: "#1e293b",
    marginBottom: 6,
  },
  welcomeText: {
    fontSize: "0.85rem",
    color: "#64748b",
    lineHeight: 1.6,
    marginBottom: 20,
  },
  quickChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 8,
    cursor: "pointer",
    fontSize: "0.83rem",
    color: "#1e293b",
    fontWeight: 500,
    transition: "box-shadow 0.15s",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  },
  quickChipIcon: { color: theme.palette.primary.main, fontSize: 18 },
  quickChipArrow: { color: "#94a3b8", fontSize: 16, marginLeft: "auto" },
  // Tickets list
  sectionLabel: {
    padding: "12px 16px 6px",
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  ticketCard: {
    background: "#fff",
    borderRadius: 10,
    margin: "0 12px 8px",
    padding: "12px 14px",
    cursor: "pointer",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    transition: "box-shadow 0.15s",
    "&:hover": {
      boxShadow: "0 2px 10px rgba(0,0,0,0.09)",
    },
  },
  ticketCardIcon: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  ticketCardBody: { flex: 1, minWidth: 0 },
  ticketCardTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 4,
  },
  ticketCardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  statusBadge: {
    padding: "1px 7px",
    borderRadius: 8,
    fontSize: "0.65rem",
    fontWeight: 700,
  },
  ticketDate: {
    fontSize: "0.68rem",
    color: "#94a3b8",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 0",
  },
}));

const formatDate = (d) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const QUICK_ACTIONS = [
  { label: "Tenho um problema técnico", category: "tecnico" },
  { label: "Dúvida sobre minha conta", category: "conta" },
  { label: "Questão financeira / cobrança", category: "financeiro" },
  { label: "Sugerir uma funcionalidade", category: "funcionalidade" },
];

const SupportWidget = () => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [chatTicketId, setChatTicketId] = useState(null);

  // New ticket dialog
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "outro", priority: "media" });
  const [creating, setCreating] = useState(false);

  const loadTickets = useCallback(() => {
    setLoading(true);
    listMyTickets()
      .then(({ data }) => setTickets(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadBadge = useCallback(() => {
    getMyCounts()
      .then(({ data }) => setBadgeCount(data.open || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadBadge();
    const interval = setInterval(loadBadge, 60000);
    return () => clearInterval(interval);
  }, [loadBadge]);

  useEffect(() => {
    if (open) loadTickets();
  }, [open, loadTickets]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleField = (field) => (e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, [field]: v }));
  };

  const openNewWithCategory = (category) => {
    setForm({ title: "", description: "", category, priority: "media" });
    setNewOpen(true);
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Preencha título e descrição");
      return;
    }
    setCreating(true);
    try {
      await createTicket(form);
      toast.success("Chamado aberto com sucesso!");
      setNewOpen(false);
      setForm({ title: "", description: "", category: "outro", priority: "media" });
      loadTickets();
      loadBadge();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erro ao abrir chamado");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenChat = (id) => {
    setChatTicketId(id);
  };

  const activeTickets = tickets.filter((t) => t.status !== "fechado");
  const closedTickets = tickets.filter((t) => t.status === "fechado");

  return (
    <>
      {/* Botão flutuante */}
      <Tooltip title="Suporte" placement="left">
        <Fab
          color="primary"
          className={classes.fab}
          onClick={handleOpen}
        >
          <Badge badgeContent={badgeCount} color="error" max={9}>
            <HeadsetMicIcon style={{ fontSize: 26 }} />
          </Badge>
        </Fab>
      </Tooltip>

      {/* Painel lateral */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.drawerPaper }}
        ModalProps={{ keepMounted: true }}
      >
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.headerIcon}>
              <HeadsetMicIcon style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <Typography className={classes.headerTitle}>Suporte</Typography>
              <Typography className={classes.headerSubtitle}>Como podemos ajudar?</Typography>
            </div>
          </div>
          <IconButton size="small" style={{ color: "#fff" }} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Body */}
        <div className={classes.body}>
          {loading ? (
            <div className={classes.loadingBox}>
              <CircularProgress size={28} />
            </div>
          ) : tickets.length === 0 ? (
            /* Welcome state */
            <div className={classes.welcomeBox}>
              <div className={classes.welcomeIcon}>
                <HeadsetMicIcon style={{ fontSize: 30, color: "inherit" }} />
              </div>
              <Typography className={classes.welcomeTitle}>Olá! Precisa de ajuda?</Typography>
              <Typography className={classes.welcomeText}>
                Nossa equipe de suporte está pronta para te atender. Selecione o assunto ou abra um chamado:
              </Typography>
              {QUICK_ACTIONS.map((action) => (
                <div
                  key={action.category}
                  className={classes.quickChip}
                  onClick={() => openNewWithCategory(action.category)}
                >
                  <ChatBubbleOutlineIcon className={classes.quickChipIcon} />
                  <span style={{ flex: 1 }}>{action.label}</span>
                  <ChevronRightIcon className={classes.quickChipArrow} />
                </div>
              ))}
            </div>
          ) : (
            /* Ticket list */
            <>
              {activeTickets.length > 0 && (
                <>
                  <div className={classes.sectionLabel}>Chamados ativos</div>
                  {activeTickets.map((t) => (
                    <div
                      key={t.id}
                      className={classes.ticketCard}
                      onClick={() => handleOpenChat(t.id)}
                    >
                      <div className={classes.ticketCardIcon}>
                        <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
                      </div>
                      <div className={classes.ticketCardBody}>
                        <div className={classes.ticketCardTitle}>{t.title}</div>
                        <div className={classes.ticketCardMeta}>
                          <span
                            className={classes.statusBadge}
                            style={STATUS_STYLES[t.status] || {}}
                          >
                            {STATUS_LABELS[t.status] || t.status}
                          </span>
                          <span
                            className={classes.statusBadge}
                            style={PRIORITY_STYLES[t.priority] || {}}
                          >
                            {t.priority}
                          </span>
                          <span className={classes.ticketDate}>{formatDate(t.updatedAt)}</span>
                        </div>
                      </div>
                      <ChevronRightIcon style={{ color: "#cbd5e1", fontSize: 18, flexShrink: 0 }} />
                    </div>
                  ))}
                </>
              )}
              {closedTickets.length > 0 && (
                <>
                  <div className={classes.sectionLabel}>Histórico</div>
                  {closedTickets.map((t) => (
                    <div
                      key={t.id}
                      className={classes.ticketCard}
                      onClick={() => handleOpenChat(t.id)}
                    >
                      <div className={classes.ticketCardIcon}>
                        <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
                      </div>
                      <div className={classes.ticketCardBody}>
                        <div className={classes.ticketCardTitle}>{t.title}</div>
                        <div className={classes.ticketCardMeta}>
                          <span
                            className={classes.statusBadge}
                            style={STATUS_STYLES[t.status] || {}}
                          >
                            {STATUS_LABELS[t.status] || t.status}
                          </span>
                          <span className={classes.ticketDate}>{formatDate(t.updatedAt)}</span>
                        </div>
                      </div>
                      <ChevronRightIcon style={{ color: "#cbd5e1", fontSize: 18, flexShrink: 0 }} />
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddIcon />}
            style={{ textTransform: "none", borderRadius: 8, fontWeight: 600 }}
            onClick={() => {
              setForm({ title: "", description: "", category: "outro", priority: "media" });
              setNewOpen(true);
            }}
          >
            Abrir novo chamado
          </Button>
        </div>
      </Drawer>

      {/* Chat dialog */}
      <SupportTicketChat
        open={Boolean(chatTicketId)}
        onClose={() => { setChatTicketId(null); loadTickets(); loadBadge(); }}
        ticketId={chatTicketId}
        isAdmin={false}
        onUpdated={() => { loadTickets(); loadBadge(); }}
      />

      {/* New ticket dialog */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Abrir chamado de suporte</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                label="Título *"
                fullWidth
                variant="outlined"
                size="small"
                value={form.title}
                onChange={handleField("title")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Categoria"
                select
                fullWidth
                variant="outlined"
                size="small"
                value={form.category}
                onChange={handleField("category")}
              >
                <MenuItem value="tecnico">Técnico</MenuItem>
                <MenuItem value="financeiro">Financeiro</MenuItem>
                <MenuItem value="conta">Conta</MenuItem>
                <MenuItem value="funcionalidade">Funcionalidade</MenuItem>
                <MenuItem value="outro">Outro</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prioridade"
                select
                fullWidth
                variant="outlined"
                size="small"
                value={form.priority}
                onChange={handleField("priority")}
              >
                <MenuItem value="baixa">Baixa</MenuItem>
                <MenuItem value="media">Média</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="urgente">Urgente</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descreva o problema *"
                fullWidth
                multiline
                rows={5}
                variant="outlined"
                size="small"
                value={form.description}
                onChange={handleField("description")}
                placeholder="Descreva com detalhes o que está acontecendo, passos para reproduzir o problema, mensagens de erro, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)} disabled={creating}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Abrir chamado"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupportWidget;
